import { useNavigate, useLocation } from "@tanstack/react-router";
import { useCallback, useContext, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import useApiClient from "./useApiClient";
import UserContext from "../contexts/user";

const isUnauthorizedError = (error: unknown) =>
  typeof error === "object" &&
  error !== null &&
  "status" in error &&
  (error as { status?: unknown }).status === 401;

export default function useAuth(): void {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isUserLoading, setUser } = useContext(UserContext);
  const api = useApiClient();
  const isRedirectingRef = useRef(false);
  const isAuthRoute =
    location.pathname === "/auth" || location.pathname.startsWith("/auth/");

  const {
    data: userData,
    error: userDetailsError,
    isLoading: userDetailsAreLoading,
    refetch: refetchUserDetails,
  } = useQuery({
    queryKey: ["getUserDetails"],
    queryFn: api.users.usersCurrentUser.bind(api.users),
    enabled: !isAuthRoute,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) =>
      !isUnauthorizedError(error) && failureCount < 1,
    staleTime: 0,
    throwOnError: false, // Don't throw 401s to ErrorBoundary; handle the redirect below.
  });

  useEffect(() => {
    if (isAuthRoute) {
      return;
    }

    const recheckUserOnFocus = () => {
      void refetchUserDetails({ cancelRefetch: false });
    };

    window.addEventListener("focus", recheckUserOnFocus);
    return () => window.removeEventListener("focus", recheckUserOnFocus);
  }, [isAuthRoute, refetchUserDetails]);

  const redirectToAuth = useCallback(() => {
    if (isAuthRoute || isRedirectingRef.current) {
      return;
    }

    isRedirectingRef.current = true;
    setUser(null);
    window.sessionStorage.setItem(
      "redirectUrl",
      location.pathname + location.searchStr
    );
    void navigate({ to: "/auth/signIn" }).catch((err) => {
      isRedirectingRef.current = false;
      console.error("Failed to navigate:", err);
    });
  }, [isAuthRoute, location.pathname, location.searchStr, navigate, setUser]);

  useEffect(() => {
    if (isAuthRoute) {
      // We've reached the sign-in route; clear the guard so a future
      // expiry/401 on a protected route can trigger another redirect.
      isRedirectingRef.current = false;
      return;
    }

    if (isUnauthorizedError(userDetailsError)) {
      redirectToAuth();
      return;
    }

    if (userData && !userDetailsAreLoading) {
      setUser(userData);
      return;
    }

    if (isUserLoading || user || userDetailsAreLoading) {
      return;
    }

    redirectToAuth();
  }, [
    userData,
    userDetailsError,
    userDetailsAreLoading,
    isAuthRoute,
    user,
    isUserLoading,
    setUser,
    redirectToAuth,
  ]);
}
